import { STAGE } from '../../design/lab/palette';
import React from 'react';
import BlobBot from './BlobBot';
import { BotState } from './motionSystem';
import { TOOLS } from '../../agent/tools';
import AgentActivity from './AgentActivity';
import { toolActivity } from './activityStates';

export interface ToolRosterProps {
    /** Per-tool state, keyed by tool name. Absent entries render as idle. */
    states?: Record<string, BotState>;
    /** Show the one-line blurb under each tool */
    verbose?: boolean;
    size?: number;
}

const mono: React.CSSProperties = {
    fontFamily: "'JetBrains Mono','Fira Code',monospace",
};

/**
 * ToolRoster — the agent's capabilities, as a team of characters.
 *
 * Rendered straight from the tool registry, so registering a tool gives it a
 * face with no change here. Each tool owns its colour and label in `tools.ts`;
 * this component only lays them out and forwards state.
 *
 * The point is that the agent's capabilities are visible BEFORE it uses them.
 * A tool call that scrolls past in a transcript tells you what happened once;
 * a roster tells you what could happen at all.
 */
const ToolRoster: React.FC<ToolRosterProps> = ({ states = {}, verbose = false, size = 26 }) => (
    <div
        style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: verbose ? '14px' : '16px',
            alignItems: 'flex-start',
            padding: verbose ? '14px 12px' : '10px 12px',
            margin: '6px 0 10px',
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: '10px',
        }}
    >
        {!verbose && (
            <span style={{ ...mono, fontSize: '10px', color: STAGE.muted, alignSelf: 'center' }}>
                tools
            </span>
        )}
        {TOOLS.map(tool => {
            const state = states[tool.name] ?? 'idle';
            const active = state !== 'idle';
            return (
                <div
                    key={tool.name}
                    data-tool={tool.name}
                    style={{
                        display: 'flex',
                        flexDirection: verbose ? 'column' : 'row',
                        alignItems: 'center',
                        gap: verbose ? '7px' : '7px',
                        minWidth: verbose ? '92px' : undefined,
                        flex: verbose ? '1 1 92px' : undefined,
                        opacity: 1,
                        transition: 'opacity 0.3s',
                    }}
                >
                    <BlobBot color={tool.color} size={size} state={state} variant={tool.variant} />
                    <div style={{ textAlign: verbose ? 'center' : 'left' }}>
                        <div style={{
                            ...mono, fontSize: '10.5px', fontWeight: 700,
                            color: active ? tool.color : STAGE.muted,
                            transition: 'color 0.3s',
                        }}>
                            {tool.label}
                        </div>
                        {verbose && (
                            <div style={{
                                ...mono, fontSize: '9.5px', color: STAGE.muted,
                                lineHeight: 1.45, marginTop: '3px',
                            }}>
                                {tool.blurb}
                            </div>
                        )}
                        {active && (
                            <div style={{ ...mono, display: 'flex', alignItems: 'center', justifyContent: verbose ? 'center' : 'flex-start', gap: '5px', fontSize: '10px', color: state === 'blocked' ? STAGE.red : STAGE.muted }}>
                                <AgentActivity state={state} work={toolActivity(tool.name)} theme="dark" />
                                <span>{state}</span>
                            </div>
                        )}
                    </div>
                </div>
            );
        })}
    </div>
);

export default ToolRoster;
